import axios from 'axios'
import type { QueryClient } from '@tanstack/react-query'

import type { AddressLookupAPI } from '@/shared/components/ui/address-lookup-context'
import type { PostcodeLookupResult } from '@/shared/lib/postcode-lookup'
import { unwrapApiEnvelope } from '@/shared/lib/api-envelope'
import { buildApiUrl } from '@/shared/lib/api-origin'
import { getAuthToken } from '@/shared/lib/auth-token'

// The address lookup endpoints live at /api/lookup/... (not under the consumer
// namespace), so they need their own client with the /api baseURL rather than
// reusing consumer/lib/api (which is scoped to /api/consumer). Auth is the
// consumer SPA's X-Session-Token header (same transport as consumer/lib/api) -
// NOT cookies. Without attaching it these IsAuthenticated endpoints return 403.
const lookupClient = axios.create({
  baseURL: buildApiUrl('/api'),
  withCredentials: false,
})
lookupClient.interceptors.request.use((config) => {
  const token = getAuthToken()
  if (token) {
    config.headers['X-Session-Token'] = token
  }
  return config
})
lookupClient.interceptors.response.use((response) => unwrapApiEnvelope(response))

interface ResolvedAddress {
  line_1?: string
  line_2?: string
  town_or_city?: string
  county?: string
  postcode?: string
  country?: string
}

// Provider selection is a server-side decision (the org's usable ADDRESS
// integration, else Google Places biased to the org country). The `provider`
// arg is intentionally NOT forwarded - a field's `lookup_provider` pin no longer
// overrides the tenant's own integration.
//
// Routed through the React Query cache (mirrors the admin `createAddressLookupApi`)
// so repeated autocomplete/resolve/postcode calls for the same term share the
// `address-autocomplete` / `address-resolve` / `address-postcode` cache entries
// (SEARCH policy, in-memory) instead of re-hitting the server on every keystroke
// or on-blur. Nothing is persisted to disk on the consumer surface.
export function createConsumerAddressLookupApi(
  queryClient: QueryClient,
): AddressLookupAPI {
  return {
    // ``country`` is part of the cache key AND the request (country-scoped, like
    // the backend cache) so a US search never serves a CA one and vice versa.
    autocomplete: (term, country) =>
      queryClient.fetchQuery({
        queryKey: ['address-autocomplete', country ?? '', term],
        queryFn: async () => {
          const params: Record<string, string> = { term }
          if (country) params.country = country
          const { data } = await lookupClient.get<{
            suggestions: { id: string; address: string; entries?: number }[]
          }>('/lookup/address/autocomplete/', { params })
          return data.suggestions
        },
      }),
    expand: (id) =>
      queryClient.fetchQuery({
        queryKey: ['address-expand', id],
        queryFn: async () => {
          const { data } = await lookupClient.get<{
            suggestions: { id: string; address: string; entries?: number }[]
          }>('/lookup/address/expand/', { params: { id } })
          return data.suggestions
        },
      }),
    capabilities: () =>
      queryClient.fetchQuery({
        queryKey: ['address-capabilities'],
        staleTime: 60 * 60 * 1000,
        queryFn: async () => {
          const { data } = await lookupClient.get<{
            enabled: boolean
            default_country: string
            countries: string[]
          }>('/lookup/address/capabilities/')
          return {
            enabled: data.enabled,
            defaultCountry: data.default_country ?? '',
            countries: data.countries ?? [],
          }
        },
      }),
    resolve: (id, term) =>
      queryClient.fetchQuery({
        queryKey: ['address-resolve', id, term],
        queryFn: async () => {
          const params: Record<string, string> = { id, term }
          const { data } = await lookupClient.get<ResolvedAddress>(
            '/lookup/address/resolve/',
            { params },
          )
          return {
            addressLine1: data.line_1 ?? '',
            addressLine2: data.line_2 ?? '',
            locality: data.town_or_city ?? '',
            region: data.county ?? '',
            postalCode: data.postcode ?? '',
            countryCode: data.country ?? '',
          }
        },
      }),
    lookupPostcode: (postcode, country) =>
      queryClient.fetchQuery({
        // Key on `country` directly (not `country ?? 'US'`): the request omits
        // the param when country is undefined, letting the backend resolve the
        // tenant default. Coalescing to 'US' in the key alone would file that
        // result under 'US' and let a later explicit country='US' call hit it.
        queryKey: ['address-postcode', country, postcode],
        staleTime: 60 * 60 * 1000,
        queryFn: async () => {
          const params: Record<string, string> = { postcode }
          if (country) params.country = country
          const { data } = await lookupClient.get<{
            options: PostcodeLookupResult[]
          }>('/lookup/address/postcode/', { params })
          return data.options ?? []
        },
      }),
  }
}
